import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDYesNo } from '../primitives/CDCheckbox';
import { CDCurrency, CDPercentage } from '../primitives/CDCurrency';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface LoanTermValue {
  value: number;
  canIncrease: boolean;
  increaseDetails?: string;
}

export interface LoanFeature {
  hasFeature: boolean;
  maxAmount?: number;
  details?: string;
}

export interface LoanTermsData {
  loanAmount: LoanTermValue;
  interestRate: LoanTermValue;
  monthlyPrincipalAndInterest: LoanTermValue;
  prepaymentPenalty: LoanFeature;
  balloonPayment: LoanFeature;
}

export interface CDLoanTermsProps {
  /** Loan terms data */
  data: Partial<LoanTermsData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<LoanTermsData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDLoanTerms - Loan Terms section on Page 1 matching CFPB format
 *
 * Layout:
 * - Loan Amount, Interest Rate, Monthly P&I with "Can this amount increase" column
 * - "Does the loan have these features?" divider
 * - Prepayment Penalty and Balloon Payment with YES/NO
 */
export const CDLoanTerms = React.forwardRef<HTMLDivElement, CDLoanTermsProps>(
  ({ data, editable = false, onChange, className }, ref) => {
    const loanAmount = data.loanAmount || { value: 0, canIncrease: false };
    const interestRate = data.interestRate || { value: 0, canIncrease: false };
    const monthlyPI = data.monthlyPrincipalAndInterest || { value: 0, canIncrease: false };
    const prepayment = data.prepaymentPenalty || { hasFeature: false };
    const balloon = data.balloonPayment || { hasFeature: false };

    // Helper to update loan term values
    const updateLoanTerm = (
      field: 'loanAmount' | 'interestRate' | 'monthlyPrincipalAndInterest',
      updates: Partial<LoanTermValue>,
    ) => {
      const current = data[field] || { value: 0, canIncrease: false };
      onChange?.({
        ...data,
        [field]: { ...current, ...updates },
      });
    };

    // Helper to update loan features
    const updateFeature = (
      field: 'prepaymentPenalty' | 'balloonPayment',
      updates: Partial<LoanFeature>,
    ) => {
      const current = data[field] || { hasFeature: false };
      onChange?.({
        ...data,
        [field]: { ...current, ...updates },
      });
    };

    return (
      <div ref={ref} className={cn('', className)}>
        <CDSectionHeader title="Loan Terms" />

        <div className="border border-t-0 border-cd-black">
          {/* Column Header Row */}
          <div className="grid grid-cols-[200px_120px_1fr] border-b border-cd-black">
            <div className="p-2" />
            <div className="p-2" />
            <div className="p-2 cd-text-body font-bold">
              Can this amount increase after closing?
            </div>
          </div>

          {/* Loan Amount Row */}
          <div className="grid grid-cols-[200px_120px_1fr] border-b border-cd-black">
            <div className="p-2 border-r border-cd-black cd-text-body font-bold">Loan Amount</div>
            <div className="p-2 border-r border-cd-black">
              <CDCurrency
                value={loanAmount.value}
                className="text-[14pt]"
                editable={editable}
                onChange={(v) => updateLoanTerm('loanAmount', { value: v })}
              />
            </div>
            <div className="p-2 cd-text-body">
              {editable ? (
                <div className="flex items-center gap-2">
                  <CDYesNo
                    value={loanAmount.canIncrease}
                    onChange={(v) => updateLoanTerm('loanAmount', { canIncrease: v })}
                  />
                  {loanAmount.canIncrease && (
                    <CDEditableText
                      value={loanAmount.increaseDetails}
                      editable={true}
                      onChange={(v) => updateLoanTerm('loanAmount', { increaseDetails: v })}
                      placeholder="Enter details..."
                      size="body"
                      className="flex-1"
                    />
                  )}
                </div>
              ) : (
                <>
                  <span className="font-bold">{loanAmount.canIncrease ? 'YES' : 'NO'}</span>
                  {loanAmount.canIncrease && loanAmount.increaseDetails && (
                    <span className="ml-2">
                      <span className="font-bold">&#8226;</span> {loanAmount.increaseDetails}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Interest Rate Row */}
          <div className="grid grid-cols-[200px_120px_1fr] border-b border-cd-black">
            <div className="p-2 border-r border-cd-black cd-text-body font-bold">Interest Rate</div>
            <div className="p-2 border-r border-cd-black">
              <CDPercentage
                value={interestRate.value}
                className="text-[14pt]"
                editable={editable}
                onChange={(v) => updateLoanTerm('interestRate', { value: v })}
              />
            </div>
            <div className="p-2 cd-text-body">
              {editable ? (
                <div className="flex items-center gap-2">
                  <CDYesNo
                    value={interestRate.canIncrease}
                    onChange={(v) => updateLoanTerm('interestRate', { canIncrease: v })}
                  />
                  {interestRate.canIncrease && (
                    <CDEditableText
                      value={interestRate.increaseDetails}
                      editable={true}
                      onChange={(v) => updateLoanTerm('interestRate', { increaseDetails: v })}
                      placeholder="Enter details..."
                      size="body"
                      className="flex-1"
                    />
                  )}
                </div>
              ) : (
                <>
                  <span className="font-bold">{interestRate.canIncrease ? 'YES' : 'NO'}</span>
                  {interestRate.canIncrease && interestRate.increaseDetails && (
                    <span className="ml-2">
                      <span className="font-bold">&#8226;</span> {interestRate.increaseDetails}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Monthly Principal & Interest Row */}
          <div className="grid grid-cols-[200px_120px_1fr] border-b border-cd-black">
            <div className="p-2 border-r border-cd-black">
              <div className="cd-text-body font-bold">Monthly Principal & Interest</div>
              <div className="cd-text-small text-cd-gray-text italic">
                See Projected Payments below for your
                <br />
                Estimated Total Monthly Payment
              </div>
            </div>
            <div className="p-2 border-r border-cd-black">
              <CDCurrency
                value={monthlyPI.value}
                className="text-[14pt]"
                editable={editable}
                onChange={(v) => updateLoanTerm('monthlyPrincipalAndInterest', { value: v })}
              />
            </div>
            <div className="p-2 cd-text-body">
              {editable ? (
                <div className="flex items-center gap-2">
                  <CDYesNo
                    value={monthlyPI.canIncrease}
                    onChange={(v) =>
                      updateLoanTerm('monthlyPrincipalAndInterest', { canIncrease: v })
                    }
                  />
                  {monthlyPI.canIncrease && (
                    <CDEditableText
                      value={monthlyPI.increaseDetails}
                      editable={true}
                      onChange={(v) =>
                        updateLoanTerm('monthlyPrincipalAndInterest', { increaseDetails: v })
                      }
                      placeholder="Enter details..."
                      size="body"
                      className="flex-1"
                    />
                  )}
                </div>
              ) : (
                <>
                  <span className="font-bold">{monthlyPI.canIncrease ? 'YES' : 'NO'}</span>
                  {monthlyPI.canIncrease && monthlyPI.increaseDetails && (
                    <span className="ml-2">
                      <span className="font-bold">&#8226;</span> {monthlyPI.increaseDetails}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Does the loan have these features? Divider */}
          <div className="grid grid-cols-[320px_1fr] border-b border-cd-black">
            <div className="p-2 border-r border-cd-black" />
            <div className="p-2 cd-text-body font-bold">Does the loan have these features?</div>
          </div>

          {/* Prepayment Penalty Row */}
          <div className="grid grid-cols-[320px_1fr] border-b border-cd-black">
            <div className="p-2 border-r border-cd-black cd-text-body font-bold">
              Prepayment Penalty
            </div>
            <div className="p-2 cd-text-body">
              {editable ? (
                <div className="flex items-center gap-2">
                  <CDYesNo
                    value={prepayment.hasFeature}
                    onChange={(v) => updateFeature('prepaymentPenalty', { hasFeature: v })}
                  />
                  {prepayment.hasFeature && (
                    <div className="flex items-center gap-2 flex-1">
                      <span>Max:</span>
                      <CDCurrency
                        value={prepayment.maxAmount}
                        editable={true}
                        onChange={(v) => updateFeature('prepaymentPenalty', { maxAmount: v })}
                        placeholder="Amount"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <span className="font-bold">{prepayment.hasFeature ? 'YES' : 'NO'}</span>
                  {prepayment.hasFeature && (
                    <span className="ml-2">
                      <span className="font-bold">&#8226;</span>{' '}
                      {prepayment.maxAmount
                        ? `As high as $${prepayment.maxAmount.toLocaleString()} if you pay off the loan during the first 2 years`
                        : prepayment.details}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Balloon Payment Row */}
          <div className="grid grid-cols-[320px_1fr]">
            <div className="p-2 border-r border-cd-black cd-text-body font-bold">
              Balloon Payment
            </div>
            <div className="p-2 cd-text-body">
              {editable ? (
                <div className="flex items-center gap-2">
                  <CDYesNo
                    value={balloon.hasFeature}
                    onChange={(v) => updateFeature('balloonPayment', { hasFeature: v })}
                  />
                  {balloon.hasFeature && (
                    <CDEditableText
                      value={balloon.details}
                      editable={true}
                      onChange={(v) => updateFeature('balloonPayment', { details: v })}
                      placeholder="Enter details..."
                      size="body"
                      className="flex-1"
                    />
                  )}
                </div>
              ) : (
                <>
                  <span className="font-bold">{balloon.hasFeature ? 'YES' : 'NO'}</span>
                  {balloon.hasFeature && balloon.details && (
                    <span className="ml-2">
                      <span className="font-bold">&#8226;</span> {balloon.details}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

CDLoanTerms.displayName = 'CDLoanTerms';
